import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import SkeletonBase from 'react-loading-skeleton';
import type { DateRange, LoginActivityRecord, ActivityHistoryRecord } from './types/reports';
import { reportsApi } from './api/reportsApi';
import '../../styles/reports.css';

const DATE_FILTERS: { label: string; value: DateRange }[] = [
  { label: 'Today',   value: 'today'  },
  { label: '7 Days',  value: '7d'     },
  { label: '30 Days', value: '30d'    },
  { label: 'Custom',  value: 'custom' },
];

const TODAY_STR    = new Date().toISOString().split('T')[0];
const ONE_YEAR_AGO = new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
const LAST_7_DAYS  = new Date(Date.now() -   7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
const LAST_30_DAYS = new Date(Date.now() -  30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
const PAGE_SIZE    = 25;

function fmtDisplayDate(iso: string) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[parseInt(m, 10) - 1]} ${parseInt(d, 10)}, ${y}`;
}

function fmtDateTime(utcIso: string) {
  if (!utcIso) return '—';
  try {
    return new Date(utcIso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true,
    });
  } catch {
    return utcIso;
  }
}


function TableSkeleton({ rows = 8 }: { rows?: number }) {
  return (
    <div style={{ padding: '8px 0' }}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} style={{ display: 'flex', gap: 12, padding: '8px 14px', borderBottom: '1px solid #f1f5f9' }}>
          <SkeletonBase width={150} height={10} borderRadius={4} />
          <SkeletonBase width={120} height={10} borderRadius={4} />
          <SkeletonBase width={70}  height={18} borderRadius={99} />
          <SkeletonBase width={240} height={10} borderRadius={4} />
        </div>
      ))}
    </div>
  );
}

function TableSkeletonLogin({ rows = 8 }: { rows?: number }) {
  return (
    <div style={{ padding: '8px 0' }}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} style={{ display: 'flex', gap: 12, padding: '8px 14px', borderBottom: '1px solid #f1f5f9' }}>
          <SkeletonBase width={160} height={10} borderRadius={4} />
          <SkeletonBase width={140} height={10} borderRadius={4} />
          <SkeletonBase width={200} height={10} borderRadius={4} />
        </div>
      ))}
    </div>
  );
}

function downloadLoginCSV(records: LoginActivityRecord[], filename: string) {
  const header = 'Time,First Name,Last Name,Email';
  const rows = records.map(r =>
    [fmtDateTime(r.utcTime), r.firstName, r.lastName, r.workEmail]
      .map(v => `"${(v ?? '').replace(/"/g, '""')}"`)
      .join(',')
  );
  const blob = new Blob([[header, ...rows].join('\n')], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function downloadActivityCSV(records: ActivityHistoryRecord[], filename: string) {
  const header = 'Time,Name,Email,Activity Type,Description';
  const rows = records.map(r =>
    [fmtDateTime(r.createdAt), [r.firstName, r.lastName].filter(Boolean).join(' '), r.workEmail, r.activityType, r.description]
      .map(v => `"${(v ?? '').replace(/"/g, '""')}"`)
      .join(',')
  );
  const blob = new Blob([[header, ...rows].join('\n')], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function Pagination({ page, total, pageSize, onChange }: { page: number; total: number; pageSize: number; onChange: (p: number) => void }) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  if (totalPages <= 1) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderTop: '1px solid #f1f5f9', fontSize: 12, color: '#64748b' }}>
      <span>
        Showing {((page - 1) * pageSize) + 1}–{Math.min(page * pageSize, total)} of {total.toLocaleString()}
      </span>
      <div style={{ display: 'flex', gap: 4 }}>
        <button
          style={{ padding: '3px 8px', borderRadius: 4, border: '1px solid #e2e8f0', background: page === 1 ? '#f8fafc' : '#fff', cursor: page === 1 ? 'not-allowed' : 'pointer', color: page === 1 ? '#cbd5e1' : '#374151' }}
          disabled={page === 1} onClick={() => onChange(page - 1)}>
          <i className="pi pi-chevron-left" style={{ fontSize: 10 }} />
        </button>
        <span style={{ padding: '3px 10px', background: '#eff6ff', borderRadius: 4, color: '#0057A4', fontWeight: 600 }}>
          {page} / {totalPages}
        </span>
        <button
          style={{ padding: '3px 8px', borderRadius: 4, border: '1px solid #e2e8f0', background: page === totalPages ? '#f8fafc' : '#fff', cursor: page === totalPages ? 'not-allowed' : 'pointer', color: page === totalPages ? '#cbd5e1' : '#374151' }}
          disabled={page === totalPages} onClick={() => onChange(page + 1)}>
          <i className="pi pi-chevron-right" style={{ fontSize: 10 }} />
        </button>
      </div>
    </div>
  );
}

export default function ActivityReportPage() {
  const navigate = useNavigate();

  /* ── Date filter state ── */
  const [dateRange, setDateRange] = useState<DateRange>('7d');
  const [customFrom, setCustomFrom] = useState(LAST_7_DAYS);
  const [customTo,   setCustomTo]   = useState(TODAY_STR);
  const fromRef = useRef<HTMLInputElement>(null);
  const toRef   = useRef<HTMLInputElement>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshTick,  setRefreshTick]  = useState(0);

  /* ── Login Activity state ── */
  const [loginData,    setLoginData]    = useState<{ total: number; records: LoginActivityRecord[] } | null>(null);
  const [loginLoading, setLoginLoading] = useState(true);
  const [loginError,   setLoginError]   = useState<string | null>(null);
  const [loginSearch,  setLoginSearch]  = useState('');
  const [loginPage,    setLoginPage]    = useState(1);

  /* ── Activity History state ── */
  const [actData,    setActData]    = useState<{ total: number; records: ActivityHistoryRecord[] } | null>(null);
  const [actLoading, setActLoading] = useState(true);
  const [actError,   setActError]   = useState<string | null>(null);
  const [actSearch,  setActSearch]  = useState('');
  const [actPage,    setActPage]    = useState(1);

  /* ── Date helpers ── */
  const displayFrom = dateRange === 'custom' ? customFrom
    : dateRange === 'today' ? TODAY_STR
    : dateRange === '7d'    ? LAST_7_DAYS
    : LAST_30_DAYS;
  const displayTo   = dateRange === 'custom' ? customTo : TODAY_STR;
  const customReady = dateRange !== 'custom' || (!!customFrom && !!customTo);

  function handleDateFilter(value: DateRange) {
    setDateRange(value);
    if (value === 'custom') { setCustomFrom(LAST_7_DAYS); setCustomTo(TODAY_STR); }
  }
  function handleFromSlotClick() {
    if (dateRange !== 'custom') { handleDateFilter('custom'); }
    else { fromRef.current?.showPicker(); }
  }
  function handleToSlotClick() {
    if (dateRange !== 'custom') { handleDateFilter('custom'); }
    else { toRef.current?.showPicker(); }
  }
  function handleRefresh() {
    if (isRefreshing) return;
    setIsRefreshing(true);
    setRefreshTick(t => t + 1);
    setTimeout(() => setIsRefreshing(false), 800);
  }

  /* ── Fetch login activity ── */
  useEffect(() => {
    if (!customReady) return;
    const ctrl = new AbortController();
    setLoginLoading(true); setLoginError(null); setLoginPage(1);
    const df = dateRange === 'custom' ? customFrom : undefined;
    const dt = dateRange === 'custom' ? customTo   : undefined;
    reportsApi.getLoginActivity(dateRange, ctrl.signal, df, dt)
      .then(d  => { setLoginData(d);          setLoginLoading(false); })
      .catch(e => { if (!ctrl.signal.aborted) { setLoginError(e.message); setLoginLoading(false); } });
    return () => ctrl.abort();
  }, [dateRange, customFrom, customTo, refreshTick]);

  /* ── Fetch activity history ── */
  useEffect(() => {
    if (!customReady) return;
    const ctrl = new AbortController();
    setActLoading(true); setActError(null); setActPage(1);
    const df = dateRange === 'custom' ? customFrom : undefined;
    const dt = dateRange === 'custom' ? customTo   : undefined;
    reportsApi.getActivityHistory(dateRange, ctrl.signal, df, dt)
      .then(d  => { setActData(d);          setActLoading(false); })
      .catch(e => { if (!ctrl.signal.aborted) { setActError(e.message); setActLoading(false); } });
    return () => ctrl.abort();
  }, [dateRange, customFrom, customTo, refreshTick]);

  /* ── Filtered records ── */
  const filteredLogin = useMemo(() => {
    if (!loginData) return [];
    const q = loginSearch.toLowerCase();
    if (!q) return loginData.records;
    return loginData.records.filter(r =>
      r.firstName.toLowerCase().includes(q) ||
      r.lastName.toLowerCase().includes(q)  ||
      r.workEmail.toLowerCase().includes(q)
    );
  }, [loginData, loginSearch]);

  const filteredAct = useMemo(() => {
    if (!actData) return [];
    const q = actSearch.toLowerCase();
    if (!q) return actData.records;
    return actData.records.filter(r =>
      r.firstName.toLowerCase().includes(q)   ||
      r.lastName.toLowerCase().includes(q)    ||
      r.workEmail.toLowerCase().includes(q)   ||
      r.title.toLowerCase().includes(q)       ||
      r.description.toLowerCase().includes(q) ||
      r.activityType.toLowerCase().includes(q)
    );
  }, [actData, actSearch]);

  const pagedLogin = filteredLogin.slice((loginPage - 1) * PAGE_SIZE, loginPage * PAGE_SIZE);
  const pagedAct   = filteredAct.slice((actPage - 1) * PAGE_SIZE, actPage * PAGE_SIZE);

  return (
    <div className="reports__page">

      {/* ── Header ── */}
      <div className="reports__header">
        <div className="reports__headerLeft">
          <h1>Activity Report</h1>
          <p>Agent and employee activity insights</p>
        </div>
        <div className="reports__headerActions">

          <button className="reports__backBtn" onClick={() => navigate('/agentdashboard')}>
            <i className="pi pi-arrow-left" style={{ fontSize: 10 }} />
            Back to Dashboard
          </button>

          <div className="reports__headerDivider" />

          <div className="reports__dateFilter">
            {DATE_FILTERS.map(f => (
              <button key={f.value}
                className={`reports__dateBtn${dateRange === f.value ? ' reports__dateBtn--active' : ''}`}
                onClick={() => handleDateFilter(f.value)}
              >{f.label}</button>
            ))}
          </div>

          <div className="reports__customDatePicker">
            <div
              className={`reports__cdpSlot${dateRange !== 'custom' ? ' reports__cdpSlot--readonly' : ''}`}
              onClick={handleFromSlotClick}
            >
              <div className="reports__cdpSlotContent">
                <span className="reports__cdpSlotLabel">FROM</span>
                <span className="reports__cdpSlotValue">{fmtDisplayDate(displayFrom)}</span>
              </div>
              <div className="reports__cdpCalBtn"><i className="pi pi-calendar" /></div>
              {dateRange === 'custom' && (
                <input ref={fromRef} type="date" className="reports__cdpInput"
                  min={ONE_YEAR_AGO} max={customTo || TODAY_STR}
                  value={customFrom} onChange={e => setCustomFrom(e.target.value)} />
              )}
            </div>

            <span className="reports__cdpArrow"><i className="pi pi-arrow-right" /></span>

            <div
              className={`reports__cdpSlot${dateRange !== 'custom' ? ' reports__cdpSlot--readonly' : ''}`}
              onClick={handleToSlotClick}
            >
              <div className="reports__cdpSlotContent">
                <span className="reports__cdpSlotLabel">TO</span>
                <span className="reports__cdpSlotValue">{fmtDisplayDate(displayTo)}</span>
              </div>
              <div className="reports__cdpCalBtn"><i className="pi pi-calendar" /></div>
              {dateRange === 'custom' && (
                <input ref={toRef} type="date" className="reports__cdpInput"
                  min={customFrom || ONE_YEAR_AGO} max={TODAY_STR}
                  value={customTo} onChange={e => setCustomTo(e.target.value)} />
              )}
            </div>
          </div>

          <button className="reports__xlsBtn" title="Export all data to Excel">
            <i className="pi pi-file-excel" /><span>Export Excel</span>
          </button>

          <button className="reports__iconBtn" onClick={handleRefresh} title="Refresh">
            <i className={`pi pi-refresh${isRefreshing ? ' reports__iconBtn--spinning' : ''}`} />
          </button>

        </div>
      </div>

      {dateRange === 'custom' && !customReady && (
        <div className="reports__customPrompt">
          <i className="pi pi-calendar" />
          Select a From and To date above to load the report
        </div>
      )}

      {customReady && (
        <div className="reports__gridWrap">
          <div className="reports__grid">

            {/* ── Card 1: User Login Activity ── */}
            <div className="reports__card reports__card--full">
              <div className="reports__cardHeader">
                <i className="pi pi-sign-in reports__cardIcon" />
                <span className="reports__cardTitle">User Login Activity</span>
                {loginData && !loginLoading && (
                  <span className="reports__cardBadge">
                    {loginData.total.toLocaleString()} total logins
                  </span>
                )}
              </div>

              <div className="reports__rotToolbar">
                <div className="reports__rotSearch">
                  <i className="pi pi-search reports__rotSearchIcon" />
                  <input type="text" className="reports__rotSearchInput"
                    placeholder="Search name or email…"
                    value={loginSearch}
                    onChange={e => { setLoginSearch(e.target.value); setLoginPage(1); }} />
                  {loginSearch && (
                    <button className="reports__rotSearchClear"
                      onClick={() => { setLoginSearch(''); setLoginPage(1); }} title="Clear">
                      <i className="pi pi-times" />
                    </button>
                  )}
                </div>
                <div className="reports__rotActions">
                  <button className="reports__exportBtn"
                    disabled={!loginData || loginLoading}
                    onClick={() => loginData && downloadLoginCSV(filteredLogin, `login-activity-${TODAY_STR}.csv`)}
                    title="Export to CSV">
                    <i className="pi pi-file-excel" /><span>Export</span>
                  </button>
                </div>
              </div>

              {loginLoading ? <TableSkeletonLogin rows={8} /> : loginError ? (
                <div style={{ padding: '24px 16px', color: '#ef4444', fontSize: 13 }}>
                  <i className="pi pi-exclamation-triangle" style={{ marginRight: 6 }} />{loginError}
                </div>
              ) : (
                <>
                  <div className="reports__rotTableWrap">
                    <div className="reports__rotTableHead">
                      <table>
                        <colgroup>
                          <col style={{ width: '22%' }} />
                          <col style={{ width: '28%' }} />
                          <col style={{ width: '50%' }} />
                        </colgroup>
                        <thead><tr><th>Time</th><th>Name</th><th>Email</th></tr></thead>
                      </table>
                    </div>
                    <div className="reports__rotTableBody">
                      <table>
                        <colgroup>
                          <col style={{ width: '22%' }} />
                          <col style={{ width: '28%' }} />
                          <col style={{ width: '50%' }} />
                        </colgroup>
                        <tbody>
                          {pagedLogin.length === 0 ? (
                            <tr><td colSpan={3} style={{ textAlign: 'center', padding: '24px 0', color: '#94a3b8', fontSize: 13 }}>No records found</td></tr>
                          ) : pagedLogin.map((r, i) => (
                            <tr key={i}>
                              <td>{fmtDateTime(r.utcTime)}</td>
                              <td>{[r.firstName, r.lastName].filter(Boolean).join(' ') || '—'}</td>
                              <td>{r.workEmail || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <Pagination page={loginPage} total={filteredLogin.length} pageSize={PAGE_SIZE} onChange={setLoginPage} />
                </>
              )}
            </div>

            {/* ── Card 2: Activity History ── */}
            <div className="reports__card reports__card--full">
              <div className="reports__cardHeader">
                <i className="pi pi-history reports__cardIcon" />
                <span className="reports__cardTitle">Activity History</span>
                {actData && !actLoading && (
                  <span className="reports__cardBadge">
                    {actData.total.toLocaleString()} total events
                  </span>
                )}
              </div>

              <div className="reports__rotToolbar">
                <div className="reports__rotSearch">
                  <i className="pi pi-search reports__rotSearchIcon" />
                  <input type="text" className="reports__rotSearchInput"
                    placeholder="Search user, activity, or description…"
                    value={actSearch}
                    onChange={e => { setActSearch(e.target.value); setActPage(1); }} />
                  {actSearch && (
                    <button className="reports__rotSearchClear"
                      onClick={() => { setActSearch(''); setActPage(1); }} title="Clear">
                      <i className="pi pi-times" />
                    </button>
                  )}
                </div>
                <div className="reports__rotActions">
                  <button className="reports__exportBtn"
                    disabled={!actData || actLoading}
                    onClick={() => actData && downloadActivityCSV(filteredAct, `activity-history-${TODAY_STR}.csv`)}
                    title="Export to CSV">
                    <i className="pi pi-file-excel" /><span>Export</span>
                  </button>
                </div>
              </div>

              {actLoading ? <TableSkeleton rows={8} /> : actError ? (
                <div style={{ padding: '24px 16px', color: '#ef4444', fontSize: 13 }}>
                  <i className="pi pi-exclamation-triangle" style={{ marginRight: 6 }} />{actError}
                </div>
              ) : (
                <>
                  <div className="reports__rotTableWrap">
                    <div className="reports__rotTableHead">
                      <table>
                        <colgroup>
                          <col style={{ width: '18%' }} />
                          <col style={{ width: '18%' }} />
                          <col style={{ width: '12%' }} />
                          <col style={{ width: '52%' }} />
                        </colgroup>
                        <thead>
                          <tr>
                            <th>Time</th>
                            <th>User</th>
                            <th>Activity</th>
                            <th>Description</th>
                          </tr>
                        </thead>
                      </table>
                    </div>
                    <div className="reports__rotTableBody">
                      <table>
                        <colgroup>
                          <col style={{ width: '18%' }} />
                          <col style={{ width: '18%' }} />
                          <col style={{ width: '12%' }} />
                          <col style={{ width: '52%' }} />
                        </colgroup>
                        <tbody>
                          {pagedAct.length === 0 ? (
                            <tr><td colSpan={4} style={{ textAlign: 'center', padding: '24px 0', color: '#94a3b8', fontSize: 13 }}>No records found</td></tr>
                          ) : pagedAct.map((r) => (
                            <tr key={r.id}>
                              <td>{fmtDateTime(r.createdAt)}</td>
                              <td style={{ fontSize: 12 }}>
                                <div style={{ fontWeight: 500 }}>{[r.firstName, r.lastName].filter(Boolean).join(' ') || '—'}</div>
                                <div style={{ color: '#94a3b8', marginTop: 1 }}>{r.workEmail || '—'}</div>
                              </td>
                              <td style={{ fontSize: 12 }}>{r.title}</td>
                              <td style={{ fontSize: 12, color: '#475569' }}>{r.description}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <Pagination page={actPage} total={filteredAct.length} pageSize={PAGE_SIZE} onChange={setActPage} />
                </>
              )}
            </div>

          </div>
        </div>
      )}

    </div>
  );
}
